import React, { useState } from 'react';
import Alert from 'components/ui/Alert';
import ErrorAlert from 'components/ui/ErrorAlert';
import LoadingCircle from 'components/ui/LoadingCircle';
import Container from 'components/v2/container';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import CargomaticDropzone from 'components/v2/DropZone';
import { uploadReferenceNumbers } from 'utils/ShipmentWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import FileDownload from 'react-file-download';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';

const TEMPLATE_URL =
  'https://docs.cargomatic.com/reference-numbers-template.csv';

const ReferenceNumberUpload = () => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const dropzoneStyle = {
    display: 'flex',
    height: '50px',
    width: 'auto',
    border: '1px dashed black',
    borderRadius: '4px',
    cursor: 'pointer',
  };

  function handleCloseErrorAlert() {
    setError('');
  }

  function handleCloseSuccessAlert() {
    setSuccess('');
  }

  async function handleUpload(files) {
    if (!files || files.length === 0) {
      setError(t('CSV_FILES_ALLOWED'));
      return;
    }

    setPending(true);

    try {
      const res = await uploadReferenceNumbers(files[0]);
      if (res.type === 'text/csv') {
        const { text } = res;
        setError(t('SOME_ROWS_UNPROCESSED'));
        FileDownload(text, 'shipment_ref.csv');
      } else {
        const {
          body: { rowsProcessed, rowsAttempted },
        } = res;
        const message = t('PROCESSED_CONFIRMATION_MSG', {
          rowsProcessed,
          rowsAttempted,
        });
        setSuccess(message);
      }
    } catch (e) {
      const errorMsg = e?.response?.body?.message || 'SOMETHING_WENT_WRONG';
      setError(t(sluggify(errorMsg)));
    } finally {
      setPending(false);
    }
  }

  if (!SessionStore.isAdmin()) {
    return (
      <Container className="py-10">
        <Alert title={t('NO_PERMISSION_FOR_VIEW')} type="error" />
      </Container>
    );
  }
  return (
    <Container className="py-10">
      <div className="flex space-x-4 items-center">
        <h3 className="my-0 mb-2">{t('REFERENCE_NUMBER_ADDITIONS')}</h3>
      </div>
      <p className="text-gray-500 text-sm mb-4">
        {t('DRAG_AND_DROP_FOR_REF_NUM_ADDITIONS')}
      </p>
      <a
        href={TEMPLATE_URL}
        title={t('DOWNLOAD_TEMPLATE')}
        className="button button-green flex-grow-0 flex-shrink-0 inline-block mb-8"
        download
      >
        <div className="flex space-x-1 items-center">
          <span>{t('TEMPLATE')}</span>
          <DownloadIcon className="w-5 h-5" />
        </div>
      </a>
      <div className="max-w-md">
        {error && (
          <div className="my-4">
            <ErrorAlert
              title={error}
              canClose
              onClose={handleCloseErrorAlert}
            />
          </div>
        )}
        {success && (
          <div className="my-4">
            <Alert
              type="success"
              title={t('REF_NUMS_UPLOADED')}
              canClose
              onClose={handleCloseSuccessAlert}
            >
              {success}
            </Alert>
          </div>
        )}
        {pending && (
          <div className="my-4">
            <LoadingCircle text={t('UPLOADING') + '...'} />
          </div>
        )}
      </div>
      <div className="sm:max-w-sm">
        <CargomaticDropzone
          onDrop={handleUpload}
          multiple={false}
          style={dropzoneStyle}
          dropzoneMessage={t('DROPZONE_MSG')}
          disabled={pending}
          acceptedFiles=".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values"
        />
      </div>
    </Container>
  );
};

export default ReferenceNumberUpload;
